// Public meditations archive — the blog index at /m
// Lists all published meditations chronologically. Each card is a link to the
// single-post permalink at /m/{id}. This is the canonical URL anyone linking
// to "the meditations blog" should use.

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";
import { shortLabel } from "../../utils/meditations";

const PREVIEW_LIMIT = 180;

// Reads the PII-safe summary doc maintained by the `syncPublicMeditations`
// Cloud Function — publicly readable so anonymous visitors (mobile, in-app
// browsers) see the archive.
function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((i) => ({
    id: i.id,
    type:              i.type || "pancake", // "shift" = griddle meditation on a whole day
    meditation:        i.meditation,
    subjectName:       i.subjectName,
    recipientName:     i.subjectName,
    artistName:        i.artistName,
    photo_cutout_url:  i.photoCutoutUrl,
    photo_url:         i.photoUrl,
    collage:           i.collage || [],
    pancakeCount:      i.pancakeCount ?? null,
    meditatedAt:  i.meditatedAtMs ? { toMillis: () => i.meditatedAtMs } : null,
    completedAt:  i.completedAtMs ? { toMillis: () => i.completedAtMs } : null,
    createdAt:    i.createdAtMs   ? { toMillis: () => i.createdAtMs   } : null,
  }));
}

export default function MeditationsArchive({ navigate }) {
  const [items, setItems]   = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = "Meditations · The Joy of Pancakes";
    return () => { document.title = "The Joy of Pancakes"; };
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config/publicMeditations"),
      (snap) => {
        if (!snap.exists()) { setItems([]); setLoaded(true); return; }
        const rows = normalizeItems(snap.data()?.items)
          .sort((a, b) => publishedAt(b) - publishedAt(a));
        setItems(rows);
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return unsub;
  }, []);

  return (
    <div style={{ background: "#081a2a", minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
      <SiteNav />

      <main style={{ maxWidth: "780px", margin: "0 auto", padding: "5.5rem 1.5rem 4rem" }}>
        <header style={{ textAlign: "center", marginBottom: "3rem" }}>
          <PanBar width={80} height={4} margin="0 auto 1.2rem" />
          <h1 style={{
            margin: "0 0 0.6rem",
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(2.4rem, 6vw, 3.6rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            lineHeight: 1.1,
          }}>
            Meditations.
          </h1>
          <p style={{
            margin: "0 auto",
            maxWidth: "520px",
            color: "rgba(255,218,173,0.65)",
            fontSize: "0.98rem",
            lineHeight: 1.55,
          }}>
            A record of the pancakes worth sitting with — and what I was thinking about while I made them.
          </p>
        </header>

        {!loaded && (
          <p style={{ color: "rgba(255,218,173,0.5)", textAlign: "center", padding: "2rem" }}>Loading…</p>
        )}

        {loaded && items.length === 0 && (
          <div style={{
            maxWidth: "540px", margin: "0 auto",
            border: "1px dashed rgba(252,179,31,0.35)",
            borderRadius: "12px", padding: "1.4rem",
            color: "rgba(255,218,173,0.7)",
            textAlign: "center",
            fontSize: "0.9rem",
            lineHeight: 1.5,
          }}>
            No meditations published yet. Check back soon.
          </div>
        )}

        {loaded && items.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "2rem" }}>
            {items.map((item) => <ArchiveRow key={item.id} item={item} />)}
          </ul>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function ArchiveRow({ item }) {
  const isShift = item.type === "shift";
  const img = item.photo_cutout_url || item.photo_url;
  const name = isShift ? (item.subjectName || "A day at the griddle") : shortLabel(item);
  const fullText = item.meditation?.trim() || "";
  const isLong = fullText.length > PREVIEW_LIMIT;
  const excerpt = isLong
    ? fullText.slice(0, PREVIEW_LIMIT).replace(/\s+\S*$/, "") + "…"
    : fullText;
  const isCutout = !!item.photo_cutout_url;
  const date = formatDate(publishedAt(item));
  const href = isShift ? `/m/shift/${item.id}` : `/m/${item.id}`;
  const collage = isShift ? item.collage.slice(0, 4) : [];

  return (
    <li>
      <a href={href} style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: "1.2rem",
        alignItems: "start",
        textDecoration: "none",
        color: "inherit",
        padding: "0.6rem",
        borderRadius: "12px",
        transition: "background 0.2s ease",
      }}
      className="jop-archive-row"
      >
        {/* Thumbnail — shift rows show a 2×2 mini-collage of the day's pancakes */}
        {isShift ? (
          <div style={{
            width: "120px",
            aspectRatio: "1 / 1",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center", justifyItems: "center",
            gap: "2px",
            flexShrink: 0,
          }}>
            {collage.map((p, i) => (
              <img
                key={p.orderId || i}
                src={p.thumbUrl}
                alt={p.title || "A pancake"}
                loading="lazy"
                style={{
                  maxWidth: "100%", maxHeight: "100%",
                  objectFit: "contain",
                  filter: "drop-shadow(0 5px 10px rgba(0,0,0,0.5))",
                }}
              />
            ))}
          </div>
        ) : (
        <div style={{
          width: "120px",
          aspectRatio: "1 / 1",
          background: isCutout ? "transparent" : "#000",
          borderRadius: isCutout ? 0 : "10px",
          overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {img && (
            <img
              src={img}
              alt={name}
              loading="lazy"
              style={{
                maxWidth: "100%", maxHeight: "100%",
                objectFit: "contain",
                filter: isCutout ? "drop-shadow(0 10px 20px rgba(0,0,0,0.55))" : "none",
              }}
            />
          )}
        </div>
        )}

        {/* Text */}
        <div style={{ minWidth: 0 }}>
          <h2 style={{
            margin: "0 0 0.3rem",
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
            color: "var(--jop-cream)",
            fontWeight: 700,
            lineHeight: 1.15,
          }}>
            {name}
          </h2>
          {date && (
            <div style={{
              color: "rgba(255,218,173,0.4)",
              fontSize: "0.72rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: "0.55rem",
            }}>
              {isShift && (
                <span style={{
                  color: "var(--jop-gold)",
                  border: "1px solid rgba(252,179,31,0.4)",
                  borderRadius: "999px",
                  padding: "0.08rem 0.5rem",
                  marginRight: "0.5rem",
                  fontWeight: 700,
                }}>
                  🔥 From the griddle
                </span>
              )}
              {date}
              {isShift
                ? (item.pancakeCount ? ` · ${item.pancakeCount} pancakes` : "")
                : (item.artistName ? ` · ${item.artistName}` : "")}
            </div>
          )}
          <p style={{
            margin: 0,
            color: "rgba(255,218,173,0.82)",
            fontSize: "0.95rem",
            lineHeight: 1.55,
            fontStyle: "italic",
          }}>
            {excerpt}
          </p>
          {isLong && (
            <span style={{
              display: "inline-block",
              marginTop: "0.55rem",
              color: "var(--jop-pink)",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}>
              Read meditation →
            </span>
          )}
        </div>
      </a>

      <style>{`
        .jop-archive-row:hover { background: rgba(255,218,173,0.04); }
        @media (max-width: 520px) {
          .jop-archive-row { grid-template-columns: 90px 1fr !important; gap: 0.9rem !important; }
        }
      `}</style>
    </li>
  );
}

function publishedAt(o) {
  const ts = o.meditatedAt ?? o.completedAt ?? o.createdAt;
  return ts?.toMillis?.() ?? 0;
}

function formatDate(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
